import {useParams} from 'react-router-dom';
import {PaginationResponse} from '@common/http/backend-response/pagination-response';
import {Album} from '@app/web-player/albums/album';
import {assignAlbumToTracks} from '@app/web-player/albums/assign-album-to-tracks';
import {useInfiniteData} from '@common/ui/infinite-scroll/use-infinite-data';
import { AlbumSubtype } from '@common/admin/use-album_taxonomies';

export type AlbumViewMode = 'list' | 'grid';
export type ArtistCreditRole = 'all' | 'primary' | 'secondary';

export interface ArtistAlbumQueryOptions {
  includeTrackCollaborations?: boolean;
  artistCreditRole?: ArtistCreditRole;
  includeDerivedSingles?: boolean;
}

export const albumListViewPerPage = 5;
export const albumGridViewPerPage = 25;

export function useArtistAlbums(
  initialPage: PaginationResponse<Album> | null,
  viewMode: AlbumViewMode,
  artistId?: string | number,
  options: ArtistAlbumQueryOptions = {},
) {
  const {artistId: routeArtistId} = useParams<{artistId: string}>();
  const id = artistId ?? (routeArtistId ? Number(routeArtistId) : undefined);

  return useInfiniteData<Album>({
    endpoint: `artists/${id}/albums`,
    queryKey: ['artists', +id!, 'albums', viewMode, options],
    queryParams: {
      includeTrackCollaborations: options.includeTrackCollaborations ? 1 : 0,
      artistCreditRole: options.artistCreditRole || 'all',
      includeDerivedSingles: options.includeDerivedSingles ? 1 : 0,
    },
    paginate: 'simple',
    initialPage,
    transformResponse: response => {
      response.pagination.data = response.pagination.data.map(album =>
        assignAlbumToTracks(album)
      );
      return response;
    },
  });
}
export function useArtistAlbumsSubtype(
  initialPage: PaginationResponse<Album> | null,
  viewMode: AlbumViewMode,
  subtype: string,
  artistId?: string | number,
  options: ArtistAlbumQueryOptions = {},
) {
  const {artistId: routeArtistId} = useParams<{artistId: string}>();
  const id = artistId ?? (routeArtistId ? Number(routeArtistId) : undefined);

  return useInfiniteData<Album>({
    endpoint: `artists/${id}/albums/${subtype}`,
    queryKey: ['artists', +id!, 'albums', subtype, viewMode, options],
    queryParams: {
      includeTrackCollaborations: options.includeTrackCollaborations ? 1 : 0,
      artistCreditRole: options.artistCreditRole || 'all',
      includeDerivedSingles: options.includeDerivedSingles ? 1 : 0,
    },
    paginate: 'simple',
    initialPage,
    transformResponse: response => {
      response.pagination.data = response.pagination.data.map(album =>
        assignAlbumToTracks(album)
      );
      return response;
    },
  });
}
